/**
 * RerunDiffChip — inline diff chip for the Rerun panel's primary CTA
 * (RP1).
 *
 * Replaces the right-hand "Changes for v1 → v2" sidebar with a compact
 * chip that surfaces a one-line summary (`+1 doc · +2 prefs`) adjacent
 * to the Run button. Click opens a popover with the full breakdown
 * grouped by Documents / Report-type prefs / Site project intent.
 *
 * Pure presentation. The diff is computed by `computeRerunDiff` in
 * `src/lib/rerunDiff.ts` and passed in here; this component does only
 * the chip rendering + popover toggle.
 *
 * Hidden when `summary.totalChanges === 0` — the rerun-canvas auto-skip
 * path takes over the empty-delta case via the existing RerunDeltaCard
 * landing surface.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import {
  formatRerunDiffChipCaption,
  type RerunDiffSummary,
} from '../../../lib/rerunDiff';
import {
  formatEvidenceSourceDiffCaption,
  type EvidenceSourceDiff,
} from '../../../lib/evidenceSourceDiff';

export interface RerunDiffChipProps {
  /** Pre-computed diff summary — caller owns the inputs + computation. */
  summary: RerunDiffSummary;
  /** Version arithmetic label for the popover header (e.g., "v1 → v2"). */
  versionLabel?: string;
  /** Citation_Traceability CT11 — optional evidence-source diff
   *  surfaced as a fourth popover section. The chip caption appends
   *  the evidence-delta summary ("+1 amendment · ~1 provider value")
   *  when this is non-empty so the user sees, before the rerun
   *  fires, both panel-edit and source-level changes in one place. */
  evidenceSourceDiff?: EvidenceSourceDiff;
}

export const RerunDiffChip: React.FC<RerunDiffChipProps> = ({ summary, versionLabel, evidenceSourceDiff }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape so the popover doesn't trap focus.
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Total = panel edits + evidence-source changes. Hidden when both
  // are zero — the auto-skip happy path takes over via RerunDeltaCard
  // / RR2.
  const evidenceChanges = evidenceSourceDiff?.totalChanges ?? 0;
  if (summary.totalChanges === 0 && evidenceChanges === 0) return null;

  const panelCaption = formatRerunDiffChipCaption(summary);
  const evidenceCaption = evidenceSourceDiff
    ? formatEvidenceSourceDiffCaption(evidenceSourceDiff)
    : '';
  const caption = [panelCaption, evidenceCaption].filter(s => s.length > 0).join(' · ');

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md selected-accent type-body uppercase tracking-widest hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="rerun-diff-chip"
      >
        {caption}
        <ChevronRight
          className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Rerun changes summary"
          className="absolute right-0 bottom-full mb-2 w-[280px] border border-border bg-popover text-popover-foreground shadow-lg p-3 space-y-3 z-50"
          data-testid="rerun-diff-popover"
        >
          <div className="flex items-center justify-between border-b border-border pb-1.5">
            <span className="type-body font-bold uppercase tracking-widest text-text-secondary">
              {versionLabel ? `Changes for ${versionLabel}` : 'Changes'}
            </span>
            <span className="type-meta text-text-muted">
              {summary.totalChanges} total
            </span>
          </div>

          {summary.counts.newDocs + summary.counts.removedDocs > 0 && (
            <section data-testid="rerun-diff-popover-docs-group">
              <h4 className="type-body font-bold uppercase tracking-widest text-text-secondary mb-1">
                Documents
              </h4>
              <ul className="space-y-0.5">
                {summary.breakdown.newDocs.map((e, i) => (
                  <li key={`new-${i}`} className="type-body text-success">
                    + {e.to}
                  </li>
                ))}
                {summary.breakdown.removedDocs.map((e, i) => (
                  <li key={`rm-${i}`} className="type-body text-danger line-through">
                    − {e.from}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {summary.counts.prefChanges > 0 && (
            <section data-testid="rerun-diff-popover-prefs-group">
              <h4 className="type-body font-bold uppercase tracking-widest text-text-secondary mb-1">
                Report-type preferences
              </h4>
              <ul className="space-y-0.5">
                {summary.breakdown.prefChanges.map((e, i) => (
                  <li key={`pref-${i}`} className="type-body flex items-start gap-1.5">
                    <span className="text-text-secondary shrink-0">{e.label}:</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-danger line-through">{e.from}</span>
                      <span className="text-text-muted mx-1">→</span>
                      <span className="text-success font-bold">{e.to}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {summary.counts.intentChanges > 0 && (
            <section data-testid="rerun-diff-popover-intent-group">
              <h4 className="type-body font-bold uppercase tracking-widest text-text-secondary mb-1">
                Site project intent
              </h4>
              <ul className="space-y-0.5">
                {summary.breakdown.intentChanges.map((e, i) => (
                  <li key={`intent-${i}`} className="type-body flex items-start gap-1.5">
                    <span className="text-text-secondary shrink-0">{e.label}:</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-danger line-through">{e.from}</span>
                      <span className="text-text-muted mx-1">→</span>
                      <span className="text-success font-bold">{e.to}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Citation_Traceability CT11 — evidence-source diff (added /
              removed / changed atoms vs the prior version). Surfaces
              the reproducibility guarantee line at the bottom. */}
          {evidenceSourceDiff && evidenceSourceDiff.totalChanges > 0 && (
            <section data-testid="rerun-diff-popover-evidence-group">
              <h4 className="type-body font-bold uppercase tracking-widest text-text-secondary mb-1">
                Evidence sources
              </h4>
              <ul className="space-y-0.5">
                {evidenceSourceDiff.added.map((e, i) => (
                  <li
                    key={`ev-add-${i}`}
                    className="type-body text-success"
                    data-testid={`rerun-diff-popover-evidence-added-${i}`}
                  >
                    + {e.label}
                  </li>
                ))}
                {evidenceSourceDiff.changed.map((e, i) => (
                  <li
                    key={`ev-chg-${i}`}
                    className="type-body text-warning"
                    data-testid={`rerun-diff-popover-evidence-changed-${i}`}
                  >
                    ~ {e.label}
                  </li>
                ))}
                {evidenceSourceDiff.removed.map((e, i) => (
                  <li
                    key={`ev-rm-${i}`}
                    className="type-body text-danger line-through"
                    data-testid={`rerun-diff-popover-evidence-removed-${i}`}
                  >
                    − {e.label}
                  </li>
                ))}
              </ul>
              <div
                className="mt-1.5 type-meta text-text-muted italic"
                data-testid="rerun-diff-popover-evidence-reproducibility"
              >
                This rerun is reproducible from these {evidenceSourceDiff.reproducibleFrom} source
                {evidenceSourceDiff.reproducibleFrom === 1 ? '' : 's'} changes.
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};
